import { craftingDefaults, fieldKitDefaults, techTierDefaults } from '../../../lib/s2/defaults';
import type {
  CraftingRulesDef,
  FieldKitDef,
  TechRequirementDef,
  TechTierDef,
} from '../../../lib/s2/types';
import { withDefaults } from '../../../lib/teamsDefaults';
import type { FieldErrors } from '../../../lib/validate';
import {
  CheckboxField,
  Field,
  Fieldset,
  IssueList,
  ItemPicker,
  ListEditor,
  NumberField,
  NumberInput,
  SelectField,
  SelectInput,
  StringListEditor,
  TextField,
  smallBtn,
} from '../fields';
import { useContentBundle, useContentRefs, type Option } from '../refs';
import type { FormProps } from '../types';
import { IdListEditor } from './kv';

type ReqKind = 'facility' | 'tool' | 'blueprint';
const REQ_KINDS: Option[] = [
  { value: 'facility', label: 'Facility' },
  { value: 'tool', label: 'Tool' },
  { value: 'blueprint', label: 'Blueprint' },
];

/** Which of the three a requirement is: by value first (a server bundle may spell out every field), then by key. */
function requirementKind(r: TechRequirementDef): ReqKind {
  if (r.tool) return 'tool';
  if ((r.blueprint ?? 0) > 0) return 'blueprint';
  if (r.facility) return 'facility';
  if (r.tool !== undefined) return 'tool';
  if (r.blueprint !== undefined) return 'blueprint';
  return 'facility';
}

/** A fresh requirement of one kind: exactly that kind's fields, so exactly one is set. */
function newRequirement(kind: ReqKind): TechRequirementDef {
  if (kind === 'tool') return { tool: '' };
  if (kind === 'blueprint') return { blueprint: 1 };
  return { facility: '', level: 0 };
}

/**
 * The `requires[]` list of a recipe or a base module level: each row is a facility (at a level), a workshop tool held, or an
 * account blueprint tier. Switching kind clears the others. Issues come at `base.<row>.<field>`.
 */
export function RequirementsEditor({
  value,
  onChange,
  errors,
  base,
  label = 'Requires',
}: {
  value: TechRequirementDef[] | undefined | null;
  onChange: (next: TechRequirementDef[]) => void;
  errors: FieldErrors;
  base: string;
  label?: string;
}) {
  const { baseModules } = useContentRefs();
  const modules = baseModules.map((m) => ({
    value: m.id ?? '',
    label: `${m.name || m.id} (${m.id})`,
  }));
  return (
    <ListEditor<TechRequirementDef>
      label={label}
      what="requirement"
      hint="Facility at a level, a workshop tool held, or an account blueprint tier."
      items={value ?? []}
      issues={errors[base]}
      onChange={onChange}
      make={() => newRequirement('facility')}
      row={(r, j, set) => {
        const kind = requirementKind(r);
        const at = (f: string) => errors[`${base}.${j}.${f}`];
        return (
          <div className="grid gap-2 sm:grid-cols-3">
            <div>
              <SelectInput
                label={`${label} ${j + 1} kind`}
                value={kind}
                options={REQ_KINDS}
                onChange={(k) => set(newRequirement((k ?? 'facility') as ReqKind))}
              />
              <IssueList issues={errors[`${base}.${j}`]} />
            </div>
            {kind === 'facility' && (
              <>
                <div>
                  <SelectInput
                    label={`${label} ${j + 1} facility`}
                    none="(choose)"
                    value={r.facility}
                    options={modules}
                    invalid={!!at('facility')}
                    onChange={(v) => set({ ...r, facility: v ?? '' })}
                  />
                  <IssueList issues={at('facility')} />
                </div>
                <div>
                  <NumberInput
                    label={`${label} ${j + 1} level`}
                    integer
                    min={0}
                    value={r.level ?? 0}
                    onChange={(v) => set({ ...r, level: v ?? 0 })}
                  />
                  <p className="text-xs text-bruise">0 = level 1.</p>
                  <IssueList issues={at('level')} />
                </div>
              </>
            )}
            {kind === 'tool' && (
              <div className="sm:col-span-2">
                <ItemPicker
                  label={`${label} ${j + 1} tool`}
                  none="(choose)"
                  filter={(c) => c === 'workshop_tool'}
                  value={r.tool}
                  invalid={!!at('tool')}
                  onChange={(v) => set({ ...r, tool: v ?? '' })}
                />
                <IssueList issues={at('tool')} />
              </div>
            )}
            {kind === 'blueprint' && (
              <div className="sm:col-span-2">
                <NumberInput
                  label={`${label} ${j + 1} blueprint tier`}
                  integer
                  min={1}
                  value={r.blueprint ?? 0}
                  onChange={(v) => set({ ...r, blueprint: v ?? 0 })}
                />
                <IssueList issues={at('blueprint')} />
              </div>
            )}
          </div>
        );
      }}
    />
  );
}

/** One tech tier (crafting.html): its gate facility, key parts, milestones, the quest and what a rebuild restores. */
export function TechTierForm({ value, onChange, errors }: FormProps<TechTierDef>) {
  const t = withDefaults(techTierDefaults(), value ?? {}) as Required<TechTierDef>;
  const set = <K extends keyof TechTierDef>(k: K, v: TechTierDef[K]) => onChange({ ...t, [k]: v });
  const b = useContentBundle();
  const { baseModules, missions } = useContentRefs();
  return (
    <>
      <Fieldset legend="Tier">
        <NumberField
          label="Tier"
          hint="The key of this entry."
          integer
          min={0}
          value={t.tier}
          issues={errors.tier}
          onChange={(v) => set('tier', v ?? 0)}
        />
        <TextField
          label="Name"
          value={t.name}
          issues={errors.name}
          onChange={(v) => set('name', v)}
        />
        <SelectField
          label="Gate module"
          none="(none)"
          value={t.gateModuleId}
          options={baseModules.map((m) => ({
            value: m.id ?? '',
            label: `${m.name || m.id} (${m.id})`,
          }))}
          issues={errors.gateModuleId}
          onChange={(v) => set('gateModuleId', v ?? '')}
        />
        <SelectField
          label="Quest mission"
          none="(none, tier 1)"
          value={t.questMissionId}
          options={missions.map((m) => ({
            value: m.id ?? '',
            label: m.title && m.title !== m.id ? `${m.title} (${m.id})` : (m.id ?? ''),
          }))}
          issues={errors.questMissionId}
          onChange={(v) => set('questMissionId', v ?? '')}
        />
        <NumberField
          label="Hunt difficulty add"
          hint="0..4, added to the key part hunt's raid difficulty."
          integer
          min={0}
          max={4}
          value={t.huntDifficultyAdd}
          issues={errors.huntDifficultyAdd}
          onChange={(v) => set('huntDifficultyAdd', v ?? 0)}
        />
      </Fieldset>
      <Fieldset legend="Unlocking and rebuilding">
        <IdListEditor
          label="Key parts"
          what="key part"
          base="keyParts"
          options={(b.keyParts ?? []).map((k) => ({
            value: k.id ?? '',
            label: k.name && k.name !== k.id ? `${k.name} (${k.id})` : (k.id ?? ''),
          }))}
          value={t.keyParts}
          errors={errors}
          onChange={(v) => set('keyParts', v)}
        />
        <StringListEditor
          label="Milestones"
          base="milestones"
          hint="A facility id, an item id, or module@level like armory@2."
          value={t.milestones}
          errors={errors}
          onChange={(v) => set('milestones', v)}
        />
        <IdListEditor
          label="Rebuild"
          what="recipe"
          base="rebuild"
          hint="Recipes the workshop gets back when it is rebuilt."
          options={(b.recipes ?? []).map((r) => ({
            value: r.id ?? '',
            label: r.name && r.name !== r.id ? `${r.name} (${r.id})` : (r.id ?? ''),
          }))}
          value={t.rebuild}
          errors={errors}
          onChange={(v) => set('rebuild', v)}
        />
      </Fieldset>
    </>
  );
}

/** The crafting section (crafting.html): craft times by tier, build-order limits, workshop banner, power and the tool wall. */
export function CraftingForm({ value, onChange, errors }: FormProps<CraftingRulesDef>) {
  const d = craftingDefaults();
  const base = withDefaults(d, value ?? {}) as Required<CraftingRulesDef>;
  const c = {
    ...base,
    craftSecondsByTier: base.craftSecondsByTier ?? [],
    buildOrders: { ...d.buildOrders, ...base.buildOrders },
    power: { ...d.power, ...base.power },
    room: { toolWall: [], ...base.room },
  } as Required<CraftingRulesDef> & {
    buildOrders: Required<NonNullable<CraftingRulesDef['buildOrders']>>;
    power: Required<NonNullable<CraftingRulesDef['power']>>;
    room: Required<NonNullable<CraftingRulesDef['room']>>;
  };
  const set = <K extends keyof CraftingRulesDef>(k: K, v: CraftingRulesDef[K]) =>
    onChange({ ...c, [k]: v });
  const tiers = c.craftSecondsByTier;
  const bo = (k: keyof typeof c.buildOrders, label: string) => (
    <NumberField
      key={k}
      label={label}
      integer
      min={0}
      value={c.buildOrders[k]}
      issues={errors[`buildOrders.${k}`]}
      onChange={(v) => set('buildOrders', { ...c.buildOrders, [k]: v ?? 0 })}
    />
  );
  return (
    <>
      <Fieldset legend="Craft times">
        <div className="sm:col-span-2">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold uppercase tracking-wide">
              Craft seconds by tier
            </span>
            <button
              type="button"
              className={smallBtn}
              onClick={() => set('craftSecondsByTier', [...tiers, tiers.at(-1) ?? 60])}
            >
              Add tier
            </button>
          </div>
          <p className="text-xs text-bruise">
            A step&apos;s seconds when the recipe&apos;s own is 0. Index = tier. Empty = the
            code&apos;s defaults.
          </p>
          {tiers.length === 0 && <p className="text-xs text-bruise">None (code defaults).</p>}
          <div className="mt-1 flex flex-wrap gap-2">
            {tiers.map((s, i) => (
              <div key={i} className="w-28">
                <div className="flex items-center justify-between text-xs">
                  <span className="font-mono">Tier {i}</span>
                  <button
                    type="button"
                    className={`${smallBtn} text-blam`}
                    aria-label={`Remove tier ${i}`}
                    onClick={() =>
                      set(
                        'craftSecondsByTier',
                        tiers.filter((_, j) => j !== i),
                      )
                    }
                  >
                    ✕
                  </button>
                </div>
                <NumberInput
                  label={`Tier ${i} seconds`}
                  integer
                  min={0}
                  invalid={!!errors[`craftSecondsByTier.${i}`]}
                  value={s}
                  onChange={(v) =>
                    set(
                      'craftSecondsByTier',
                      tiers.map((x, j) => (j === i ? (v ?? 0) : x)),
                    )
                  }
                />
                <IssueList issues={errors[`craftSecondsByTier.${i}`]} />
              </div>
            ))}
          </div>
          <IssueList issues={errors.craftSecondsByTier} />
        </div>
        <NumberField
          label="Workshop new (hours)"
          hint="How long the workshop banner says NEW."
          integer
          min={0}
          value={c.workshopNewHours}
          issues={errors.workshopNewHours}
          onChange={(v) => set('workshopNewHours', v ?? 0)}
        />
        <CheckboxField
          label="START needs presence on the phone"
          hint="A phone must be at Home or an outpost to start a craft (Home only for facilities and fuel). The browser keeps remote access."
          checked={c.startRequiresPresence}
          onChange={(v) => set('startRequiresPresence', v)}
        />
      </Fieldset>
      <Fieldset legend="Build orders">
        {bo('maxRunning', 'Max running')}
        {bo('maxWaiting', 'Max waiting')}
        {bo('maxSteps', 'Max steps')}
        {bo('maxCrafts', 'Max crafts')}
        {bo('maxQuantity', 'Max quantity')}
      </Fieldset>
      <Fieldset legend="Power">
        <Field label="Fuel item" issues={errors['power.fuelItemId']}>
          {(id, invalid) => (
            <ItemPicker
              id={id}
              invalid={invalid}
              none="(none)"
              value={c.power.fuelItemId}
              onChange={(v) => set('power', { ...c.power, fuelItemId: v ?? '' })}
            />
          )}
        </Field>
        <NumberField
          label="Wh per fuel"
          integer
          min={0}
          value={c.power.whPerFuel}
          issues={errors['power.whPerFuel']}
          onChange={(v) => set('power', { ...c.power, whPerFuel: v ?? 0 })}
        />
      </Fieldset>
      <Fieldset legend="Workshop room">
        <ListEditor<string>
          label="Tool wall"
          what="tool"
          hint="The back wall's hooks, in order."
          items={c.room.toolWall}
          issues={errors['room.toolWall']}
          onChange={(v) => set('room', { ...c.room, toolWall: v })}
          make={() => ''}
          row={(tool, j, setTool) => (
            <>
              <ItemPicker
                label={`Tool wall ${j + 1}`}
                filter={(cat) => cat === 'workshop_tool'}
                value={tool}
                invalid={!!errors[`room.toolWall.${j}`]}
                onChange={(v) => setTool(v ?? '')}
              />
              <IssueList issues={errors[`room.toolWall.${j}`]} />
            </>
          )}
        />
      </Fieldset>
    </>
  );
}

/** The field kit section: the phone's field-craft card. */
export function FieldKitForm({ value, onChange, errors }: FormProps<FieldKitDef>) {
  const f = withDefaults(fieldKitDefaults(), value ?? {}) as Required<FieldKitDef>;
  const set = <K extends keyof FieldKitDef>(k: K, v: FieldKitDef[K]) => onChange({ ...f, [k]: v });
  const b = useContentBundle();
  const fieldRecipes = (b.recipes ?? [])
    .filter((r) => r.where === 'field')
    .map((r) => ({
      value: r.id ?? '',
      label: r.name && r.name !== r.id ? `${r.name} (${r.id})` : (r.id ?? ''),
    }));
  return (
    <Fieldset legend="Field kit">
      <IdListEditor
        label="Recipes"
        what="recipe"
        base="recipeIds"
        hint="The card's order. Empty = every field recipe by id. Only recipes with where: field are offered."
        options={fieldRecipes}
        value={f.recipeIds}
        errors={errors}
        onChange={(v) => set('recipeIds', v)}
      />
      <NumberField
        label="Max times"
        hint="The most made at once."
        integer
        min={1}
        value={f.maxTimes}
        issues={errors.maxTimes}
        onChange={(v) => set('maxTimes', v ?? 0)}
      />
    </Fieldset>
  );
}
